import "amazon-connect-streams";
import { getLogger } from "@expert/logging";

import type { DataKeysContext, EncryptionInfo } from "@/encryption/types";
import type { ChatMessage, MessageSender, MessageType, ReceiptStatus } from "@/sdk/agent/chat";

import { getRootDispatcher } from "@/analytics/rootDispatcher";
import { CHAT_RUNTIME_ACTIONS, emitChatRuntimeError } from "@/sdk/agent/chat/emitChatRuntimeError";

import type { ChatConnectMessage, ChatMessageJsonString, ChatMessagePayload, EncryptionDataFields } from "../types";

import normalizePartner from "../../normalizePartner";
import { CHAT_CONTENT_TYPES } from "../types";
import { isValidMessageContent } from "./messageValidator";

const logger = getLogger({ module: "amazon-connect-message-processor" });
const analyticsDispatcher = getRootDispatcher();

interface ProcessedContent {
    content: string;
    from: string;
    contentType: string;
    senderType: MessageSender;
    triggeredComponent?: string;
    attributes?: Record<string, unknown>;
    serverReceivedTimestamp?: string;
}

/** Complete processed message result including all transformation stages. */
export interface ProcessedChatMessage {
    /** Provider-specific intermediate format (Amazon Connect internal representation) */
    connectMessage: ChatConnectMessage;
    /** Universal normalized format (used by UI, cache, and all providers) */
    normalizedMessage: ChatMessage;
}

/**
 * Parses and validates ChatMessagePayload from Amazon Connect's ChatEventData.Content field.
 *
 * @throws Error if content cannot be parsed or validated (sanitized, no content included)
 */
function parseMessageContent(data: connect.ChatEventData, messageId: string): ChatMessagePayload {
    try {
        const parsed = typeof data.Content === "string" ? JSON.parse(data.Content) : data.Content;

        if (!isValidMessageContent(parsed, messageId)) {
            throw new Error(`Invalid message content structure for message ${messageId}`);
        }

        return parsed;
    } catch {
        logger.error({ messageId }, "Failed to parse or validate message content");
        throw new Error(`Failed to process message ${messageId}`);
    }
}

/** Process application/json content type messages using the standardized structure. */
function processJsonContent(data: connect.ChatEventData, messageId: string): ProcessedContent {
    const parsed = parseMessageContent(data, messageId);

    if (parsed.triggeredComponent) {
        return {
            content: parsed.body ?? "",
            from: parsed.author,
            contentType: parsed.triggeredComponent,
            senderType: parsed.senderType,
            triggeredComponent: parsed.triggeredComponent,
            attributes: {
                amountPerMonth: parsed.amountPerMonth,
                programDisplayName: parsed.programDisplayName,
                programDescription: parsed.programDescription,
                programSku: parsed.programSku,
                partner: parsed.partner,
                callToAction: parsed.callToAction,
            },
            serverReceivedTimestamp: parsed.metadata?.serverReceivedTimestamp,
        };
    }

    return {
        content: parsed.body,
        from: parsed.author,
        contentType: parsed.type || "text",
        senderType: parsed.senderType,
        serverReceivedTimestamp: parsed.metadata?.serverReceivedTimestamp,
    };
}

/**
 * Processes Amazon Connect chat events into ChatConnectMessage and normalized ChatMessage formats.
 * Only supports application/json content type. Filter system events before calling.
 */
export function processIncomingChatMessage(data: connect.ChatEventData): ProcessedChatMessage | null {
    const messageTimestamp = data.AbsoluteTime ? new Date(data.AbsoluteTime) : new Date();
    const role = data.ParticipantRole;
    const messageId = data.Id;
    const senderName = data.DisplayName;

    logger.debug({ messageId, contentType: data.ContentType, participantRole: role }, "Processing incoming chat event");

    // Gracefully handle system events by checking against our known list of non-message content types.
    const knownEventContentTypes = Object.values(CHAT_CONTENT_TYPES);
    if (knownEventContentTypes.includes(data.ContentType as (typeof knownEventContentTypes)[number])) {
        logger.debug(
            { messageId: data.Id, contentType: data.ContentType },
            "Skipping known system event (e.g., participant joined, typing)",
        );
        return null;
    }

    // Process actual messages, handling supported and unsupported types.
    let processed: ProcessedContent;
    if (data.ContentType === "application/json") {
        try {
            processed = processJsonContent(data, messageId);
        } catch (error) {
            // TODO @asurion-core/expert-workspace-messaging, should this be a different log level?
            logger.warn(
                { error, messageId },
                "Failed to process JSON message - skipping message instead of throwing to allow other messages to process",
            );
            return null;
        }
    } else if (data.ContentType === "text/plain") {
        logger.warn(
            { messageId, contentType: "text/plain", participantRole: role },
            "Plain text message not supported - integration issue",
        );
        return null;
    } else {
        logger.warn(
            { messageId, contentType: data.ContentType, participantRole: role },
            "Unsupported or unknown content type, skipping message.",
        );
        return null;
    }

    const connectMessage: ChatConnectMessage = {
        id: messageId,
        content: processed.content,
        timestamp: messageTimestamp,
        direction: role === "CUSTOMER" ? "inbound" : "outbound",
        from: processed.from,
        participantRole: role,
        contentType: processed.contentType,
    };

    const receiptStatus = getReceiptStatus(data);

    const normalizedMessage: ChatMessage = {
        id: connectMessage.id,
        // Use timestamp as index for proper ordering (Amazon Connect doesn't provide sequential indexes)
        index: messageTimestamp.getTime(),
        senderId: connectMessage.from,
        senderName,
        body: connectMessage.content,
        senderType: processed.senderType,
        messageType: (processed.triggeredComponent as MessageType) ?? (connectMessage.contentType as MessageType),
        dateCreated: connectMessage.timestamp,
        receiptStatus,
        serverReceivedTimestamp: processed.serverReceivedTimestamp,
        attributes: processed.attributes as Record<string, unknown>,
    };

    return { connectMessage, normalizedMessage };
}

/**
 * Amazon Connect SendMessage parameters.
 * This is the format expected by connect.AgentChatSession.sendMessage()
 */
export interface ConnectSendMessageParams {
    /** Content type - always "application/json" for our standardized messages */
    contentType: "application/json";
    message: ChatMessageJsonString;
}

/**
 * Creates a ChatMessagePayload for sending text messages.
 *
 * @internal Used by createConnectSendMessageParams
 */
function processOutgoingChatMessage(
    message: string,
    metadata: {
        author: string;
        agentId: string;
        senderName: string;
        partner: string;
        sourceApp: string;
        topic: string;
        language: string;
    },
    encryption: EncryptionDataFields,
): ChatMessagePayload {
    return {
        author: metadata.author,
        body: message,
        sourceApp: metadata.sourceApp,
        senderType: "expert",
        senderName: metadata.senderName,
        type: "text",
        partner: metadata.partner,
        topic: metadata.topic,
        language: metadata.language,
        encrypted: encryption.encrypted,
        IV: encryption.IV,
        encryptedDataKey: encryption.encryptedDataKey,
        expertMetadata: {
            employeeId: metadata.author,
            displayName: metadata.senderName,
            ccaasExpertId: metadata.agentId,
        },
    };
}

/**
 * Creates complete parameters for Amazon Connect's sendMessage() call.
 *
 * Combines payload creation, JSON stringification, and transport format.
 * Returns the exact object needed for `connection.sendMessage()`.
 *
 * @example
 * const params = createConnectSendMessageParams("Hello!", metadata);
 * await connection.sendMessage(params);
 */
export function createConnectSendMessageParams(
    message: string,
    metadata: {
        author: string;
        agentId: string;
        senderName: string;
        partner: string;
        sourceApp: string;
        topic: string;
        language: string;
    },
    encryption: EncryptionDataFields,
): ConnectSendMessageParams {
    const payload = processOutgoingChatMessage(message, metadata, encryption);

    return {
        contentType: "application/json",
        message: JSON.stringify(payload) as ChatMessageJsonString,
    };
}

/**
 * Determines whether a message has been read by the other party.
 * ChatJS pre-filters receipts via `shouldShowMessageReceiptForCurrentParticipantId` before firing
 * `onReadReceipt`/`onDeliveredReceipt`, so any `ReadTimestamp` here belongs to another participant.
 */
function getReceiptStatus(data: connect.ChatEventData): ReceiptStatus {
    const hasReadReceipt = data.MessageMetadata?.Receipts?.some((receipt) => receipt.ReadTimestamp);
    return hasReadReceipt ? "read" : "delivered";
}

export type GetDecryptFn = (
    context: DataKeysContext,
    encryptedDataKey: string,
) => Promise<(info: EncryptionInfo) => Promise<string>>;

export async function decryptMessageAndHandleErrors({
    content,
    getDecrypt,
    encryptionContext,
    fallbackPartner,
    messageType,
    messageId,
    contactId,
}: {
    content: string;
    getDecrypt: GetDecryptFn;
    encryptionContext: string;
    fallbackPartner: string;
    messageType: string;
    messageId: string;
    contactId?: string;
}): Promise<string> {
    try {
        const payload = JSON.parse(content);

        const isEncrypted = payload.encrypted && payload.IV && payload.encryptedDataKey && payload.body;
        if (!isEncrypted) {
            return payload.body ?? "";
        }

        const rawPartner = payload.partner || fallbackPartner;
        if (!rawPartner) {
            logger.error({ contactId, messageId }, "Cannot decrypt: missing partner");
            // Attribute to the contact the message is for (contactId === sessionId for Connect chat).
            // Without it, the dispatcher back-fills SessionId from the globally-selected chat.
            void analyticsDispatcher
                .withIdentities(contactId ? { SessionId: contactId } : {})
                .dispatchBusinessEvent("ChatDecryptionFailed", { messageType, errorReason: "missing_partner" });
            emitChatRuntimeError(CHAT_RUNTIME_ACTIONS.messageDecryptFailed);
            return payload.body;
        }

        const partner = normalizePartner(rawPartner) ?? rawPartner.toLowerCase();

        try {
            const decrypt = await getDecrypt(
                { chatId: encryptionContext, partner },
                payload.encryptedDataKey as string,
            );
            return await decrypt({ encryptedData: payload.body as string, IV: payload.IV as string });
        } catch (error) {
            logger.error({ error, encryptionContext, partner }, "Message decryption failed");
            void analyticsDispatcher
                .withIdentities(contactId ? { SessionId: contactId } : {})
                .withExtra({ partner })
                .dispatchBusinessEvent("ChatDecryptionFailed", { messageType, errorReason: "decryption_error" });
            emitChatRuntimeError(CHAT_RUNTIME_ACTIONS.messageDecryptFailed, partner);
            return "encrypted";
        }
    } catch {
        return content;
    }
}
